import React, { useState } from 'react';
import Button from './ui/Button';
import { ArrowLeft, ArrowRight, Target } from 'lucide-react';

const ClassSelectionWizard = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selections, setSelections] = useState({
    background: '',
    language: '',
    goals: []
  });

  const totalSteps = 3;

  // Step 1: Background options
  const backgroundOptions = [
    {
      value: 'english_speaker',
      label: 'I primarily speak English'
    },
    {
      value: 'mandarin_speaker', 
      label: 'I primarily speak Mandarin'
    },
    {
      value: 'cantonese_speaker',
      label: 'I primarily speak Cantonese'
    },
    {
      value: 'multilingual',
      label: "I'm bilingual/multilingual"
    }
  ];

  // Step 2: Language options (changes based on background)
  const getLanguageOptions = () => {
    switch (selections.background) {
      case 'mandarin_speaker':
        return [
          { value: 'cantonese', label: 'Cantonese (Traditional Chinese)', description: 'Learn Cantonese with your Mandarin knowledge' }
        ];
      case 'cantonese_speaker':
        return [
          { value: 'mandarin', label: 'Mandarin (Putonghua/Simplified Chinese)', description: 'Learn Mandarin with your Cantonese knowledge' }
        ];
      default:
        return [
          { value: 'mandarin', label: 'Mandarin (Putonghua/Simplified Chinese)', description: 'Most widely spoken Chinese language' },
          { value: 'cantonese', label: 'Cantonese (Traditional Chinese)', description: 'Spoken in Hong Kong, Guangdong, and overseas communities' },
          { value: 'both', label: "I'm not sure / Both interest me", description: 'Explore options for both languages' }
        ];
    }
  };

  // Step 3: Goals options  
  const goalOptions = [
    {
      value: 'conversation',
      label: 'Casual conversation with friends/family',
      description: 'Everyday speaking and listening skills'
    },
    {
      value: 'business', 
      label: 'Business communication and networking',
      description: 'Professional language for work settings'
    },
    {
      value: 'exam_prep',
      label: 'Prepare for a specific exam (HSK, IB, IGCSE, etc.)',
      description: 'Structured test preparation'
    },
    {
      value: 'academic_support',
      label: 'Help with school homework and assignments', 
      description: 'Support for students in Chinese classes'
    },
    {
      value: 'general',
      label: 'General language improvement',
      description: 'Overall language development'
    }
  ];

  const handleSelection = (field, value) => {
    setSelections(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGoalToggle = (goalValue) => {
    setSelections(prev => ({
      ...prev,
      goals: prev.goals.includes(goalValue)
        ? prev.goals.filter(g => g !== goalValue)
        : [...prev.goals, goalValue]
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selections.background;
      case 2:
        return selections.language;
      case 3:
        return selections.goals.length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete wizard
      onComplete(selections);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "What's your background?";
      case 2:
        return "What do you want to learn?";
      case 3:
        return "What are your goals?";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case 1:
        return "This helps us recommend the right classes for you";
      case 2:
        return "Choose the language you'd like to focus on";
      case 3:
        return "Select all that apply - this helps us match you with the perfect class";
      default:
        return "";
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">
            Step {currentStep} of {totalSteps}
          </span>
          <button 
            onClick={onSkip}
            className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
          >
            Skip and browse all classes
          </button>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-2">
          <div
            className="bg-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{getStepTitle()}</h2>
        <p className="text-gray-400">{getStepSubtitle()}</p>
      </div>

      {/* Step 1: Background */}
      {currentStep === 1 && (
        <div className="space-y-4">
          {backgroundOptions.map((option) => (
            <div
              key={option.value}
              className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                selections.background === option.value
                  ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                  : 'border-gray-600 bg-gray-800 bg-opacity-50 hover:border-gray-500'
              }`}
              onClick={() => handleSelection('background', option.value)}
            >
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                  selections.background === option.value
                    ? 'border-purple-500 bg-purple-500'
                    : 'border-gray-500'
                }`}>
                  {selections.background === option.value && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-white">{option.label}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 2: Language */}
      {currentStep === 2 && (
        <div className="space-y-4">
          {getLanguageOptions().map((option) => (
            <div
              key={option.value}
              className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                selections.language === option.value
                  ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                  : 'border-gray-600 bg-gray-800 bg-opacity-50 hover:border-gray-500'
              }`}
              onClick={() => handleSelection('language', option.value)}
            >
              <div className="flex items-start">
                <div className={`w-5 h-5 rounded-full border-2 mr-4 mt-0.5 flex items-center justify-center ${
                  selections.language === option.value
                    ? 'border-purple-500 bg-purple-500'
                    : 'border-gray-500'
                }`}>
                  {selections.language === option.value && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-white mb-1">{option.label}</h3>
                  <p className="text-sm text-gray-400">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 3: Goals */}
      {currentStep === 3 && (
        <div className="space-y-4">
          {goalOptions.map((option) => (
            <div
              key={option.value}
              className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                selections.goals.includes(option.value)
                  ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                  : 'border-gray-600 bg-gray-800 bg-opacity-50 hover:border-gray-500'
              }`}
              onClick={() => handleGoalToggle(option.value)}
            >
              <div className="flex items-start">
                <div className={`w-5 h-5 rounded border mr-4 mt-0.5 flex items-center justify-center ${
                  selections.goals.includes(option.value)
                    ? 'border-purple-500 bg-purple-500'
                    : 'border-gray-500'
                }`}>
                  {selections.goals.includes(option.value) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-white mb-1">{option.label}</h3>
                  <p className="text-sm text-gray-400">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between mt-8">
        <Button
          variant="secondary"
          onClick={handleBack}
          disabled={currentStep === 1}
          className="bg-gray-700 hover:bg-gray-600 border border-gray-600 flex items-center"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>

        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!canProceed()}
          className="bg-purple-600 hover:bg-purple-700 flex items-center"
        >
          {currentStep === totalSteps ? (
            <>
              <Target size={16} className="mr-2" />
              Find My Classes
            </>
          ) : (
            <>
              Next
              <ArrowRight size={16} className="ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Selection summary */}
      {(selections.background || selections.language || selections.goals.length > 0) && (
        <div className="mt-8 p-4 bg-gray-800 bg-opacity-50 rounded-lg border border-gray-700">
          <h4 className="font-medium text-white mb-2">Your selections:</h4>
          <div className="text-sm text-gray-300 space-y-1">
            {selections.background && (
              <div>
                <span className="text-gray-400">Background:</span> {backgroundOptions.find(o => o.value === selections.background)?.label}
              </div>
            )}
            {selections.language && (
              <div>
                <span className="text-gray-400">Language:</span> {getLanguageOptions().find(o => o.value === selections.language)?.label}
              </div>
            )}
            {selections.goals.length > 0 && (
              <div>
                <span className="text-gray-400">Goals:</span> {selections.goals.map(goal => 
                  goalOptions.find(o => o.value === goal)?.label
                ).join(', ')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassSelectionWizard;